import { defineComponent as _defineComponent } from 'vue'
import { fastDeliveryGoal } from 'globalState/goals/';
    import { onMounted, ref, computed, watch, nextTick, shallowRef } from 'vue';
    import { YandexMap, YandexMarker } from 'vue-yandex-maps';
    import AutocompleteInput from 'vueCommon/components/autocomplete/Input.vue';
    import ValidatedInput from 'vueCommon/ui/ValidatedInput.vue';
    import { useMapConfig } from 'vueCommon/composables/useMapConfig.ts';
    import {
        useAddressAutocomplete,
        useFastDeliveryStatus,
        useAddressValidation,
        DeliveryStatus,
    } from '../composables/index.ts';

    
export default /*@__PURE__*/_defineComponent({
  __name: 'AddressClarification',
  props: {
        detailedDeliveryTimes: {
            type: String,
            default: '',
        },
        defaultAddress: {
            type: Object,
            default: () => ({}),
        },
        defaultAvailable: {
            type: Boolean,
            default: false,
        },
        defaultStore: {
            type: String,
            default: '',
        },
        realPrice: {
            type: [String, Number],
            default: '',
        },
        realDeliveryText: {
            type: String,
            default: '',
        },
        callbackCheckCoords: {
            type: Function,
            default: () => {},
        },
        externalCheckingStores: {
            type: Boolean,
            default: false,
        },
        mode: {
            type: String,
            default: 'fast',
            validator: (v: string) => ['fast', 'courier'].includes(v),
        },
    },
  emits: [
        'handle-set-data',
        'handle-close',
        'handle-clarification-apartment',
    ],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

    const emits = __emit;
    const props = __props;

    const isFastDelivery = computed(() => props.mode === 'fast');
    const isChecking = computed(() =>
        isFastDelivery.value ? processCheckingAddress.value : address.process
    );

    const {
        address,
        coordinates,
        zip,
        city,
        settlement,
        street,
        house,
        korp,
        build,
        fetchSuggestions,
        setSuggestion,
        clearSuggestions,
        checkAddress,
        checkCoordinates,
    } = useAddressAutocomplete();

    const {
        availableDeliveryStatus,
        processCheckingAddress,
        nearestStore,
        warehouseCode,
        availableDeliveryText,
        availableDeliveryIcon,
        checkDeliveryAvailability,
        setDeliveryStatus,
        setNearestStore,
        resetDeliveryStatus,
    } = useFastDeliveryStatus();

    const appart = ref('');

    const { canProceed, validationMessage } = useAddressValidation(
        house,
        appart,
        isFastDelivery
    );

    const focusState = ref<boolean>(false);

    const {
        settings,
        controls,
        detailedControls,
        options,
        MAP_CONSTANTS,
        getDefaultCenter,
        coordinatesToArray,
    } = useMapConfig();

    const mapCenter = ref<[number, number]>(getDefaultCenter());
    const mapZoom = ref<number>(MAP_CONSTANTS.DEFAULT_ZOOM);
    const markerCoords = ref<[number, number] | null>(null);

    /** Instance ymaps.Map из vue-yandex-maps — для явного центрирования при открытии */
    const ymapInstance = shallowRef<unknown>(null);

    const markerOptions = {
        preset: 'islands#redDotIcon',
    };

    /**
     * Явно двигает вид карты к маркеру. panTo из watcher YandexMap при первом открытии
     * часто не успевает / не срабатывает сразу после init.
     */
    const syncMapToMarker = () => {
        const map = ymapInstance.value as {
            setCenter?: (..._args: unknown[]) => void;
        } | null;
        if (!map?.setCenter || !markerCoords.value) {
            return;
        }
        map.setCenter(markerCoords.value, MAP_CONSTANTS.SELECTED_ZOOM, {
            duration: 0,
        });
        mapCenter.value = [...markerCoords.value];
        mapZoom.value = MAP_CONSTANTS.SELECTED_ZOOM;
    };

    const onMapCreated = (map: unknown) => {
        ymapInstance.value = map;
        initPropsAfterMap();
        nextTick(() => {
            syncMapToMarker();
        });
    };

    /**
     * Клик по карте: новая точка адреса (как в legacy useYandexMap)
     * Для courier — только заполняет поля, не закрывает modal.
     */
    const onMapClick = (e: { get: (_key: string) => number[] }) => {
        const coords = e.get('coords');
        if (!coords?.length) {
            return;
        }

        if (isFastDelivery.value) fastDeliveryGoal.selectedPointByMap();
        focusState.value = false;
        address.suggestions = [];
        appart.value = '';

        coordinates.lat = String(coords[0]);
        coordinates.lng = String(coords[1]);

        markerCoords.value = [coords[0], coords[1]];
        mapCenter.value = [coords[0], coords[1]];
        mapZoom.value = MAP_CONSTANTS.SELECTED_ZOOM;

        checkCoordinates().then(() => {
            if (isFastDelivery.value) {
                getAvailableStores();
            }
            // courier: координаты и адрес уже заполнены, ждём "Продолжить"
        });
    };

    const initPropsAfterMap = () => {
        if (!props.defaultAddress.city) {
            const currentCityElement: any =
                document.querySelector('.current-geo');
            city.value = currentCityElement?.innerText;
        }

        const hasAddress = !!props.defaultAddress?.house;
        const needStore = isFastDelivery.value ? !!props.defaultStore : true;
        if (props.defaultAddress && hasAddress && needStore) {
            city.value = props.defaultAddress.city;
            settlement.value = props.defaultAddress.settlement;
            street.value = props.defaultAddress.street;
            house.value = props.defaultAddress.house;
            appart.value = props.defaultAddress.appart || '';
            korp.value = props.defaultAddress.korp || '';
            build.value = props.defaultAddress.build || '';
            address.value = `${city.value ? `г. ${city.value}` : ''} ${
                settlement.value ? `${settlement.value}` : ''
            } ${street.value ?? ''} ${house.value ? `д. ${house.value}` : ''}${
                korp.value ? ` к. ${korp.value}` : ''
            } ${build.value ? ` стр. ${build.value}` : ''}`;

            if (
                props.defaultAddress.coordinates &&
                props.defaultAddress.coordinates.lat &&
                props.defaultAddress.coordinates.lng
            ) {
                coordinates.lat = props.defaultAddress.coordinates.lat;
                coordinates.lng = props.defaultAddress.coordinates.lng;

                const arr = coordinatesToArray(coordinates);
                markerCoords.value = arr;
                mapCenter.value = arr;
                mapZoom.value = MAP_CONSTANTS.SELECTED_ZOOM;
            }

            if (isFastDelivery.value) {
                if (props.defaultAvailable) {
                    setDeliveryStatus(DeliveryStatus.AVAILABLE);
                } else {
                    setDeliveryStatus(DeliveryStatus.UNAVAILABLE);
                }
                if (props.defaultStore) {
                    setNearestStore(props.defaultStore);
                }
            }
        }
    };

    watch(
        () => props.defaultStore,
        (newVal) => {
            if (newVal) {
                setNearestStore(newVal);
            }
        }
    );

    /** Адрес из cookie приходит после mount / повторное открытие модалки */
    watch(
        () => [
            props.defaultAddress?.coordinates?.lat,
            props.defaultAddress?.coordinates?.lng,
        ],
        () => {
            if (!ymapInstance.value) {
                return;
            }
            initPropsAfterMap();
            nextTick(() => syncMapToMarker());
        }
    );

    onMounted(() => {
        if (isFastDelivery.value) fastDeliveryGoal.openModal();
    });

    watch(
        () => props.defaultAvailable,
        (newVal) => {
            if (newVal) {
                setDeliveryStatus(DeliveryStatus.AVAILABLE);
            } else {
                setDeliveryStatus(DeliveryStatus.UNAVAILABLE);
            }
        }
    );

    const formActive = computed(() => {
        return house.value || address.suggestions.length !== 0;
    });

    const processAutocomplete = computed(() => {
        return address.process || (!!address.value && focusState.value);
    });

    const selectSuggestionAddress = (suggestion: any) => {
        if (isFastDelivery.value) fastDeliveryGoal.selectedPointByAddress();
        setSuggestionAddress(suggestion);
    };

    const setSuggestionAddress = (suggestion: any) => {
        focusState.value = false;
        clearSuggestions();
        if (isFastDelivery.value) resetDeliveryStatus();

        setSuggestion(suggestion);
        appart.value = '';

        if (!suggestion.house) {
            fetchSuggestionsAddress(suggestion.mainValue);
            return;
        }

        checkAddress().then(() => {
            if (isFastDelivery.value) {
                getAvailableStores();
            }
            // courier: не вызываем setFinalData — закрытие только по "Продолжить"
        });
    };

    const getRegion = (): string =>
        city.value ||
        (props.defaultAddress as any)?.city ||
        (
            document.querySelector('.delivery-info__region') as HTMLElement
        )?.innerText?.trim() ||
        '';

    const fetchSuggestionsAddress = (query: string) => {
        const region = getRegion();

        if (isFastDelivery.value) {
            fetchSuggestions(query, ['Москва', 'Московская']);
        } else {
            fetchSuggestions(query, [], region ? [region] : []);
        }
    };

    const getAvailableStores = () => {
        checkDeliveryAvailability(coordinates, props.callbackCheckCoords).then(
            ({ store }) => {
                if (store) {
                    setFinalData();
                }
            }
        );
    };

    const setConfirmedApartment = () => {
        emits('handle-clarification-apartment', appart.value);
    };

    const setFinalData = () => {
        emits('handle-set-data', {
            address: {
                warehouse: warehouseCode.value,
                zip: zip.value,
                city: city.value,
                settlement: settlement.value,
                street: street.value,
                house: house.value,
                appart: appart.value,
                korp: korp.value,
                build: build.value,
                coordinates: {
                    lat: coordinates.lat,
                    lng: coordinates.lng,
                },
            },
            availableFastDelivery:
                availableDeliveryStatus.value === DeliveryStatus.AVAILABLE,
            nearestStore: nearestStore.value,
        });
    };

    const closeModal = () => {
        emits('handle-close');
    };

    const onContinueClick = () => {
        if (!canProceed.value || isChecking.value) return;
        if (!isFastDelivery.value && (!coordinates.lat || !coordinates.lng))
            return;
        if (!isFastDelivery.value) {
            setFinalData();
        }
        closeModal();
    };

const __returned__ = { emits, props, isFastDelivery, isChecking, address, coordinates, zip, city, settlement, street, house, korp, build, fetchSuggestions, setSuggestion, clearSuggestions, checkAddress, checkCoordinates, availableDeliveryStatus, processCheckingAddress, nearestStore, warehouseCode, availableDeliveryText, availableDeliveryIcon, checkDeliveryAvailability, setDeliveryStatus, setNearestStore, resetDeliveryStatus, appart, canProceed, validationMessage, focusState, settings, controls, detailedControls, options, MAP_CONSTANTS, getDefaultCenter, coordinatesToArray, mapCenter, mapZoom, markerCoords, ymapInstance, markerOptions, syncMapToMarker, onMapCreated, onMapClick, initPropsAfterMap, formActive, processAutocomplete, selectSuggestionAddress, setSuggestionAddress, getRegion, fetchSuggestionsAddress, getAvailableStores, setConfirmedApartment, setFinalData, closeModal, onContinueClick, get YandexMap() { return YandexMap }, get YandexMarker() { return YandexMarker }, AutocompleteInput, ValidatedInput, get DeliveryStatus() { return DeliveryStatus } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})